import React, { useState, useEffect } from 'react';
import axios from 'axios';
import NavigationSecurity from '../../Layouts/Navigation/NavigationSecurity';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWater, faBuilding, faClock, faCheckCircle,
  faCalendarDays, faRefresh, faPhone
} from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../../utils/constants';

const SecurityRequests = () => {
  const [data, setData] = useState({ piscina: [], salon: [], fecha: '' });
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState('');

  const fmt = (t) => t ? t.substring(0, 5) : '';

  const fetchToday = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BASE_URL}/amenities/today`);
      setData(r.data);
      setLastUpdate(new Date().toLocaleTimeString('es-CO'));
    } catch (e) {
      console.error('Error cargando solicitudes:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchToday();
    const interval = setInterval(fetchToday, 60000);
    return () => clearInterval(interval);
  }, []);

  const ReservationCard = ({ r, type }) => {
    const isPool = type === 'piscina';
    const color = isPool ? 'blue' : 'purple';
    return (
      <div className={`bg-white rounded-xl shadow-sm border-l-4 border-${color}-400 p-4 hover:shadow-md transition-shadow`}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className={`p-2 bg-${color}-100 rounded-lg`}>
              <FontAwesomeIcon icon={isPool ? faWater : faBuilding} className={`h-4 w-4 text-${color}-600`} />
            </div>
            <div>
              <p className={`font-bold text-${color}-700 text-sm`}>
                {isPool ? 'Piscina' : 'Salón Comunal'}
              </p>
              <p className="text-xs text-gray-500">
                {r.torre_nombre} · Apto {r.num_apartamento}
              </p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-semibold">
            <FontAwesomeIcon icon={faCheckCircle} className="h-3 w-3" />
            Confirmada
          </span>
        </div>

        <div className="mt-3 space-y-1.5">
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <FontAwesomeIcon icon={faClock} className="h-3.5 w-3.5 text-gray-400" />
            <span className="font-semibold">{fmt(r.hora_inicio)} – {fmt(r.hora_fin)}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <FontAwesomeIcon icon={faPhone} className="h-3.5 w-3.5 text-gray-400" />
            <span>{r.inquilino_nombre} {r.inquilino_apellido} · {r.inquilino_telefono}</span>
          </div>
          {!isPool && r.motivo && (
            <div className="text-xs text-gray-500 bg-gray-50 px-3 py-1.5 rounded-lg mt-1">
              <span className="font-medium">Motivo:</span> {r.motivo}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <NavigationSecurity />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-5xl mx-auto">

          {/* Header */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-slate-500 to-blue-700 rounded-xl shadow">
                  <FontAwesomeIcon icon={faCalendarDays} className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">Solicitudes del día</h1>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {data.fecha || new Date().toISOString().split('T')[0]} ·
                    Actualizado: {lastUpdate}
                  </p>
                </div>
              </div>
              <button onClick={fetchToday} disabled={loading}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors shadow">
                <FontAwesomeIcon icon={faRefresh} className={loading ? 'animate-spin' : ''} />
                Actualizar
              </button>
            </div>
          </div>

          {/* Resumen */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow border-l-4 border-blue-400 p-4 flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FontAwesomeIcon icon={faWater} className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Reservas piscina hoy</p>
                <p className="text-2xl font-bold text-blue-700">{data.piscina?.length || 0}</p>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow border-l-4 border-purple-400 p-4 flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FontAwesomeIcon icon={faBuilding} className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Reservas salón hoy</p>
                <p className="text-2xl font-bold text-purple-700">{data.salon?.length || 0}</p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-3"></div>
              <p className="text-gray-500">Cargando reservas...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Piscina */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <FontAwesomeIcon icon={faWater} className="h-5 w-5 text-blue-500" />
                  <h2 className="text-lg font-bold text-gray-700">Piscina</h2>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold ml-1">
                    {data.piscina?.length || 0}
                  </span>
                </div>
                <div className="space-y-3">
                  {(data.piscina?.length || 0) === 0 ? (
                    <div className="text-center py-10 text-gray-400 bg-white rounded-xl border border-dashed border-gray-200">
                      <FontAwesomeIcon icon={faWater} className="h-8 w-8 mb-2 opacity-30" />
                      <p className="text-sm">Sin reservas de piscina hoy</p>
                    </div>
                  ) : (
                    data.piscina.map(r => (
                      <ReservationCard key={r.id_reserva} r={r} type="piscina" />
                    ))
                  )}
                </div>
              </div>

              {/* Salón */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <FontAwesomeIcon icon={faBuilding} className="h-5 w-5 text-purple-500" />
                  <h2 className="text-lg font-bold text-gray-700">Salón Comunal</h2>
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-semibold ml-1">
                    {data.salon?.length || 0}
                  </span>
                </div>
                <div className="space-y-3">
                  {(data.salon?.length || 0) === 0 ? (
                    <div className="text-center py-10 text-gray-400 bg-white rounded-xl border border-dashed border-gray-200">
                      <FontAwesomeIcon icon={faBuilding} className="h-8 w-8 mb-2 opacity-30" />
                      <p className="text-sm">Sin reservas de salón hoy</p>
                    </div>
                  ) : (
                    data.salon.map(r => (
                      <ReservationCard key={r.id_reserva} r={r} type="salon" />
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default SecurityRequests;
